import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { get } from '@sama/micro-dash';
import { lastValueFrom } from 'rxjs';
import { BlobService } from '../../../cross-team-shared/asset-loading/blob.service';
import { messageAlreadyShown } from '../../../cross-team-shared/messaging/misc';
import { NumberIndex } from '../../../cross-team-shared/types/misc';
import {
  createTempCanvas,
  displayImageWithCanvas,
} from '../../../shared/misc-utilities/image-utils';
import { RasterUrls } from '../../../team-apis/otterdam/outputs/image-annotation-output';
import { ImageContentState } from '../state/image-content-state';
import { ImageContentStore } from '../state/image-content-store';

@Injectable()
export class RasterMaskSavingService {
  private savedMask?: Uint8Array;

  constructor(
    private blobService: BlobService,
    private http: HttpClient,
    private store: ImageContentStore,
  ) {}

  async saveDraftToS3() {
    return this.saveToS3(true).then((answer) => {
      this.setLastSavedMask(this.store.state().rawMask);
      return answer;
    });
  }

  async saveToS3(isDraft = false) {
    if (!this.shouldSaveMask(isDraft)) {
      return undefined;
    }

    // convert raw mask to PNG blob
    const canvas = createTempCanvas();
    displayImageWithCanvas(rasterStateToImageData(this.store.state()), canvas);
    const blob = await this.blobService.canvasToBlob(canvas);

    // upload blob to S3
    const urls = this.getRasterUrls();
    let getUrl: string;
    let putUrl: string;
    if (isDraft) {
      [getUrl, putUrl] = [urls.draft_url, urls.signed_draft_url_for_save];
    } else {
      [getUrl, putUrl] = [urls.mask_url, urls.signed_mask_url_for_save];
    }
    try {
      await lastValueFrom(this.http.put(putUrl, blob));
    } catch (e) {
      return Promise.reject(messageAlreadyShown);
    }
    return getUrl;
  }

  setLastSavedMask(mask?: Uint8Array) {
    this.savedMask = mask;
  }

  private getRasterUrls() {
    const urls: RasterUrls | undefined = get(this.store.state(), [
      'taskExtras',
      'output_extras',
      this.store.state().output.id as NumberIndex,
      'raster_urls',
    ]);
    if (!urls) {
      throw new Error('Server did not provide a signed URL for this output');
    }

    return urls;
  }

  private shouldSaveMask(isDraft: boolean) {
    return !isDraft || this.store.getRawMask() !== this.savedMask;
  }
}

function rasterStateToImageData(state: ImageContentState) {
  const rgba = new Uint8ClampedArray(
    4 * state.sensorRendererState.width * state.sensorRendererState.height,
  );
  for (let i = state.rawMask!.length; i--; ) {
    const code = state.rawMask![i];
    const i2 = i * 4;
    rgba[i2] = code;
    rgba[i2 + 1] = code;
    rgba[i2 + 2] = code;
    rgba[i2 + 3] = 255;
  }
  return new ImageData(
    rgba,
    state.sensorRendererState.width,
    state.sensorRendererState.height,
  );
}
